import Link from 'next/link';
import { Heart, MessageCircle, Pencil, Trash2, Repeat2, Quote } from 'lucide-react';
import { Checkmark } from '@/components/ui/checkmark';
import type { SocialComment, SocialPost, SocialProfile } from '@/lib/social/types';
import { socialPostPath, socialProfilePath } from '@/lib/social/links';
import { Avatar, Timestamp } from '../ui';
import { RichPostText } from '../rich-text';
import { CommentMedia } from '../comment-preview';

type ReplyCardProps = {
  comment: SocialComment;
  post: SocialPost;
  parentAuthor: SocialProfile;
  viewerId?: string;
  busy: boolean;
  childCount: number;
  expanded: boolean;
  onReply: () => void;
  onLike: () => void;
  onRepost: () => void;
  onQuote: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onResolve: () => void;
  onToggleReplies: () => void;
};

export function ReplyCard({ comment, post, parentAuthor, viewerId, busy, childCount, expanded, onReply, onLike, onRepost, onQuote, onEdit, onDelete, onResolve, onToggleReplies }: ReplyCardProps) {
  const shareable = comment.canRepost ?? (post.visibility === 'public' && !post.author.private && !comment.author.private && !post.archived);
  const originalAuthor = comment.author.id === post.author.id;
  const feedback = comment.lane && comment.lane !== 'conversation';
  const resolved = comment.resolution && comment.resolution !== 'open';
  const own = viewerId === comment.author.id;

  return <article id={`comment-${comment.id}`} data-comment-id={comment.id} data-post-interactive className="social-reply-card scroll-mt-24">
    <Link href={socialProfilePath(comment.author.handle)} aria-label={`Profile of ${comment.author.displayName}`} className="shrink-0">
      <Avatar profile={comment.author} className="size-10" />
    </Link>
    <div className="min-w-0 flex-1">
      <div className="social-post-byline">
        <Link href={socialProfilePath(comment.author.handle)} className="social-post-identity">
          <span className="social-post-name">{comment.author.displayName}</span>
          <span className="social-post-handle">@{comment.author.handle}</span>
        </Link>
        <Link href={comment.permalink || `${socialPostPath(post)}#comment-${comment.id}`} aria-label="View reply" className="shrink-0 hover:underline"><Timestamp value={comment.createdAt} /></Link>
      </div>
      {(originalAuthor || comment.editedAt || feedback || resolved) && <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {originalAuthor && <span className="font-medium" title="Author of the original post">Author</span>}
        {comment.editedAt && <span>edited</span>}
        {feedback && <span>{comment.lane === 'keep' ? 'Keep' : comment.lane === 'try' ? 'Try' : 'Question'}</span>}
        {resolved && <span className="inline-flex items-center gap-1 font-semibold">{comment.resolution === 'implemented' ? <><Checkmark className="size-3" />Implemented</> : 'Considering'}</span>}
      </div>}
      <p className="mt-1 text-xs text-muted-foreground">Replying to <Link href={socialProfilePath(parentAuthor.handle)} className="hover:underline">@{parentAuthor.handle}</Link></p>
      {comment.deleted ? <p className="mt-2 text-sm italic text-muted-foreground">Comment removed</p> : <>
        <div className="social-post-copy mt-2"><RichPostText body={comment.body} className="text-sm" /></div>
        <CommentMedia comment={comment} />
        <div className="social-reply-actions">
          {!post.commentsDisabled && <button type="button" disabled={(comment.depth || 0) >= 3 || busy} aria-haspopup="dialog" onClick={onReply}><MessageCircle />Reply</button>}
          <button type="button" disabled={busy} aria-label={`${comment.liked ? 'Unlike' : 'Like'} comment by ${comment.author.displayName}`} aria-pressed={comment.liked} onClick={onLike}><Heart className={comment.liked ? 'fill-current text-foreground' : ''} />{comment.likes || ''}</button>
          {shareable && <>
            <button type="button" disabled={busy} aria-label="Repost comment" title="Repost reply" onClick={onRepost}><Repeat2 />{comment.reposts || ''}</button>
            <button type="button" disabled={busy} aria-label="Quote comment" title="Quote reply" onClick={onQuote}><Quote /></button>
          </>}
          {own && <button type="button" aria-label="Edit comment" title="Edit reply" disabled={busy} onClick={onEdit}><Pencil /></button>}
          {(own || viewerId === post.author.id) && <button type="button" aria-label="Delete comment" title="Delete comment" disabled={busy} onClick={onDelete}><Trash2 /></button>}
          {viewerId === post.author.id && feedback && <button type="button" disabled={busy} onClick={onResolve}><Checkmark />{comment.resolution === 'implemented' ? 'Reopen' : 'Mark implemented'}</button>}
        </div>
      </>}
      {childCount > 0 && <button type="button" className="mt-1 min-h-8 text-xs font-semibold" onClick={onToggleReplies}>{expanded ? 'Hide replies' : `Show ${childCount} ${childCount === 1 ? 'reply' : 'replies'}`}</button>}
    </div>
  </article>;
}
